import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { MessageCircle } from "lucide-react";
import MessageBubble from "./MessageBubble";
import AttachmentPreviewModal from "./AttachmentPreviewModal";
import { formatDayDivider } from "./messagingUi";
import { SkeletonBlock, SkeletonRegion } from "../skeletons/ShopperSkeletons";

function getMineSenderType(actorType) {
  return actorType === "SELLER" ? "SELLER" : "SHOPPER";
}

export default function MessageList({
  messages = [],
  conversationKey = "",
  actorType = "SHOPPER",
  loading = false,
  onDelete,
  canDeleteMessage,
  pendingDeleteId = null,
  emptyTitle = "No messages yet",
  emptyDescription = "Send a message to start the conversation.",
  loadingTitle = "Loading messages",
  loadingDescription = "Fetching conversation history.",
  className = "",
}) {
  const scrollRegionRef = useRef(null);
  const messageContentRef = useRef(null);
  const stickToLatestRef = useRef(true);
  const mineSenderType = getMineSenderType(actorType);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const handlePreviewAttachment = useCallback((att) => setPreviewAttachment(att), []);
  const latestMessage = messages[messages.length - 1] || null;

  const scrollToLatest = useCallback(() => {
    const scrollRegion = scrollRegionRef.current;
    if (!scrollRegion) return;
    scrollRegion.scrollTop = scrollRegion.scrollHeight;
  }, []);

  const handleScroll = useCallback((event) => {
    const scrollRegion = event.currentTarget;
    const distanceFromBottom = scrollRegion.scrollHeight - scrollRegion.clientHeight - scrollRegion.scrollTop;
    stickToLatestRef.current = distanceFromBottom <= 24;
  }, []);

  useLayoutEffect(() => {
    if (loading || messages.length === 0) return undefined;

    stickToLatestRef.current = true;
    scrollToLatest();

    const animationFrameId = typeof window.requestAnimationFrame === "function"
      ? window.requestAnimationFrame(scrollToLatest)
      : null;

    return () => {
      if (animationFrameId != null && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [conversationKey, latestMessage?.created_at, latestMessage?.id, loading, messages.length, scrollToLatest]);

  useLayoutEffect(() => {
    if (loading || messages.length === 0 || typeof ResizeObserver !== "function") return undefined;

    const messageContent = messageContentRef.current;
    if (!messageContent) return undefined;

    const observer = new ResizeObserver(() => {
      if (stickToLatestRef.current) scrollToLatest();
    });
    observer.observe(messageContent);

    return () => observer.disconnect();
  }, [conversationKey, loading, messages.length, scrollToLatest]);

  if (loading) {
    return (
      <SkeletonRegion label={loadingTitle} className={`min-h-0 flex-1 space-y-4 overflow-hidden px-4 py-5 ${className}`}>
        <SkeletonBlock className="h-12 w-3/5 rounded-2xl rounded-bl-md" />
        <SkeletonBlock className="ml-auto h-16 w-2/3 rounded-2xl rounded-br-md" />
        <SkeletonBlock className="h-10 w-2/5 rounded-2xl rounded-bl-md" />
        <SkeletonBlock className="ml-auto h-12 w-1/2 rounded-2xl rounded-br-md" />
        <span className="sr-only">{loadingDescription}</span>
      </SkeletonRegion>
    );
  }

  if (messages.length === 0) {
    return (
      <div className={`flex flex-1 flex-col items-center justify-center px-5 py-8 text-center text-neutral-400 ${className}`}>
        <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-800">
          <MessageCircle size={28} className="opacity-40" />
        </div>
        <p className="text-[13px] font-medium">{emptyTitle}</p>
        <p className="mt-1 max-w-xs text-[11px] leading-relaxed opacity-75">{emptyDescription}</p>
      </div>
    );
  }

  return (
    <div
      ref={scrollRegionRef}
      data-message-scroll-region="true"
      onScroll={handleScroll}
      className={`min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-3 py-4 [scrollbar-gutter:stable] sm:px-4 sm:py-5 ${className}`}
    >
      <div ref={messageContentRef}>
        {messages.map((message, index) => {
          const isMine = message.sender_type === mineSenderType;
          const currentDay = formatDayDivider(message.created_at);
          const previousDay = index > 0 ? formatDayDivider(messages[index - 1].created_at) : null;
          const showDivider = index === 0 || currentDay !== previousDay;

          return (
            <div key={message.id}>
              {showDivider && (
                <div className="my-4 flex items-center justify-center">
                  <span className="select-none rounded-full bg-neutral-100 px-3 py-1 text-[10px] text-neutral-400 dark:bg-neutral-800">
                    {currentDay}
                  </span>
                </div>
              )}
              <MessageBubble
                message={message}
                isMine={isMine}
                actorType={actorType}
                onPreviewAttachment={handlePreviewAttachment}
                canDelete={Boolean(canDeleteMessage?.(message))}
                pendingDelete={pendingDeleteId === message.id}
                onDelete={onDelete}
              />
            </div>
          );
        })}
      </div>
      {previewAttachment && (
        <AttachmentPreviewModal
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
    </div>
  );
}
